import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';

import { AppState } from '../app.state';
import { User } from '../../models/user.model';
import { LoginData, LoginResponse, FixaResource, SelectedContractDetails } from '../../models/login.model';
import { UserByUuidResponse } from '../../models/user.model';
import { CartInitService } from '../cart/cart-init.service';

import { UserState } from './user.state';
import {
  selectUserState,
  selectIsAuthenticated,
  selectLoginData,
  selectUserUuid,
  selectCurrentUser,
  selectLoading,
  selectError,
  selectLastUpdated,
  selectSelectedContract,
  selectSelectedContractDetails
} from './user.selectors';
import * as UserActions from './user.actions';

@Injectable({
  providedIn: 'root'
})
export class UserFacade {
  private store = inject(Store<AppState>);
  private cartInitService = inject(CartInitService);

  // Seletores do store
  getAuthState(): Observable<UserState> {
    return this.store.select(selectUserState);
  }

  getIsAuthenticated(): Observable<boolean> {
    return this.store.select(selectIsAuthenticated);
  }

  getUserDetails(): Observable<User | null> {
    return this.store.select(selectCurrentUser);
  }

  getLoginData(): Observable<LoginData | null> {
    return this.store.select(selectLoginData);
  }

  getIsLoading(): Observable<boolean> {
    return this.store.select(selectLoading);
  }

  getError(): Observable<string | null> {
    return this.store.select(selectError);
  }

  getUserUuid(): Observable<string | null> {
    return this.store.select(selectUserUuid);
  }

  getLastUpdated(): Observable<number | null> {
    return this.store.select(selectLastUpdated);
  }

  getSelectedContract(): Observable<FixaResource | null> {
    return this.store.select(selectSelectedContract);
  }

  getSelectedContractDetails(): Observable<SelectedContractDetails | null> {
    return this.store.select(selectSelectedContractDetails);
  }

  // Actions - Login
  login(credentials: any) {
    this.store.dispatch(UserActions.login({ credentials }));
  }

  loginSuccess(response: LoginResponse) {
    this.store.dispatch(UserActions.loginSuccess({ response }));
    // Limpa o carrinho do localStorage após login bem-sucedido
    this.cartInitService.clearStoredCartOnLogin();
  }

  loginFailure(error: string) {
    this.store.dispatch(UserActions.loginFailure({ error }));
  }

  // Actions - User Details
  loadUserDetails(uuid: string) {
    this.store.dispatch(UserActions.loadUserDetails({ uuid }));
  }

  loadUserDetailsSuccess(user: UserByUuidResponse) {
    this.store.dispatch(UserActions.loadUserDetailsSuccess({ user }));
  }

  loadUserDetailsFailure(error: string) {
    this.store.dispatch(UserActions.loadUserDetailsFailure({ error }));
  }

  // Actions - Logout
  logout() {
    this.store.dispatch(UserActions.logout());
  }

  logoutSuccess() {
    this.store.dispatch(UserActions.logoutSuccess());
  }

  // Actions - Loading
  setLoading(loading: boolean) {
    this.store.dispatch(UserActions.setLoading({ loading }));
  }

  // Actions - Select CNPJ
  selectCnpj(document: string) {
    this.store.dispatch(UserActions.selectCnpj({ document }));
  }

  // Actions - Select Contract (Fixa)
  setSelectedContract(contract: FixaResource) {
    this.store.dispatch(UserActions.selectContract({ contract }));
  }

  setSelectedContractSuccess(contractDetails: SelectedContractDetails) {
    this.store.dispatch(UserActions.selectContractSuccess({ contractDetails }));
  }

  updateLoginData(loginData: Partial<LoginData>) {
    this.store.dispatch(UserActions.updateLoginData({ loginData }));
  }
}